import { Component, ChangeDetectionStrategy, OnInit, inject, computed, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { FieldCardComponent } from '../../pages/fields/components/field-card.component';

@Component({
  selector: 'app-anchor-fields-sidebar',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    FieldCardComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (collapsed()) {
      <div class="h-full w-12 flex flex-col items-center pt-3 border-l border-zinc-200 dark:border-zinc-700 bg-zinc-50/30 dark:bg-zinc-800/30">
        <button
          data-testid="anchor-fields-sidebar-expand-btn"
          mat-icon-button
          matTooltip="Show anchor fields"
          aria-label="Show anchor fields"
          (click)="toggle()"
          class="!text-zinc-500 hover:!text-zinc-700 dark:!text-zinc-400 dark:hover:!text-zinc-200"
        >
          <mat-icon>menu</mat-icon>
        </button>
        @if (anchorFields().length > 0) {
          <span class="mt-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
            {{ anchorFields().length }}
          </span>
        }
      </div>
    } @else {
      <div class="h-full w-80 flex flex-col gap-4 border-l border-zinc-200 dark:border-zinc-700 bg-zinc-50/30 dark:bg-zinc-800/30 p-4 overflow-hidden">
        <div class="flex items-center justify-between gap-2 pb-2 border-b border-zinc-200 dark:border-zinc-700">
          <div class="flex items-center gap-2 min-w-0">
            <mat-icon class="text-indigo-600 !text-xl flex-none">push_pin</mat-icon>
            <h2 data-testid="anchor-fields-sidebar-heading"
                class="text-lg font-semibold text-zinc-900 dark:text-white truncate">Anchor Fields</h2>
            <span data-testid="anchor-fields-sidebar-count"
                  class="ml-1 rounded-full bg-zinc-100 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 flex-none">
              {{ anchorFields().length }}
            </span>
          </div>
          <button
            data-testid="anchor-fields-sidebar-collapse-btn"
            mat-icon-button
            matTooltip="Hide anchor fields"
            aria-label="Hide anchor fields"
            (click)="toggle()"
            class="!text-zinc-500 hover:!text-zinc-700 dark:!text-zinc-400 dark:hover:!text-zinc-200 flex-none"
          >
            <mat-icon>menu</mat-icon>
          </button>
        </div>

        @if (loading()) {
          <div class="flex justify-center py-8">
            <mat-spinner diameter="32"></mat-spinner>
          </div>
        } @else if (anchorFields().length === 0) {
          <div class="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800/50 p-6 text-center">
            <div class="mb-2 rounded-full bg-zinc-100 dark:bg-zinc-700 p-2">
              <mat-icon class="text-zinc-400 dark:text-zinc-500 !text-xl">push_pin</mat-icon>
            </div>
            <h3 data-testid="anchor-fields-sidebar-empty"
                class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No anchor fields</h3>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 max-w-[200px]">
              Fields with the "anchor" category will appear here.
            </p>
          </div>
        } @else {
          <div class="flex-1 space-y-3 overflow-y-auto pr-1 -mr-1">
            @for (field of anchorFields(); track field.id) {
              <app-field-card
                class="block"
                [field]="field"
                [allFields]="allFields()"
                [actionable]="false"
              />
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `]
})
export class AnchorFieldsSidebarComponent implements OnInit {
  readonly store = inject(OcrPrimeStore);

  collapsed = signal(true);

  allFields = computed(() => this.store.fields());
  loading = computed(() => this.store.fieldsLoading());
  anchorFields = computed(() =>
    this.allFields().filter(f => f.category === 'anchor')
  );

  ngOnInit(): void {
    this.store.loadFields();
  }

  toggle(): void {
    this.collapsed.update(v => !v);
  }
}
